'use client';

import { useCallback, useState, useTransition, type ReactNode } from 'react';

import { useRouter, useSearchParams } from 'next/navigation';

import type { z } from 'zod';

import { useMonitoring } from '@kit/monitoring/hooks';
import { useSignInWithEmailPassword } from '@kit/supabase/hooks/use-sign-in-with-email-password';

import { useCaptcha } from '../captcha/client';
import { useLastAuthMethod } from '../hooks/use-last-auth-method';
import type { PasswordSignInSchema } from '../schemas/password-sign-in.schema';
import { associateCustomerWithTenantAction } from '../server/associate-customer-action';
import {
  identifyAccountAction,
  type IdentifyAccountResult,
} from '../server/identify-account-action';
import { AuthErrorAlert } from './auth-error-alert';
import { EmailIdentifyForm, type EmailOnlyValues } from './email-identify-form';
import { PasswordSignInForm } from './password-sign-in-form';
import { UnknownAccountCard } from './unknown-account-card';

type Step =
  | { kind: 'email' }
  | { kind: 'password'; email: string };

export function PasswordSignInContainer({
  onSignIn,
  captchaSiteKey,
  signUpPath = '/auth/sign-up',
  onSwitchToPhone,
  footer,
}: {
  onSignIn?: (userId?: string) => unknown;
  captchaSiteKey?: string;
  signUpPath?: string;
  // Provided by the methods container when the phone method is enabled —
  // the unknown-account card's "try phone instead" action (native parity).
  onSwitchToPhone?: () => void;
  // Rendered below the FIRST screen only (the email identify step) — the
  // methods container passes its alternative-methods section here so the
  // switcher disappears once the user has progressed into the flow.
  footer?: ReactNode;
}) {
  const router = useRouter();
  const params = useSearchParams();
  const captcha = useCaptcha({ siteKey: captchaSiteKey });
  const signInMutation = useSignInWithEmailPassword();
  const { recordAuthMethod } = useLastAuthMethod();
  const monitoring = useMonitoring();
  const [step, setStep] = useState<Step>({ kind: 'email' });
  // Persist the most recently entered email across step transitions so
  // "Use a different email" doesn't lose what the user already typed.
  // Seeded from ?email so sign-up claim banners (and the native claim flow)
  // can land the user here with the address already filled in.
  const [lastEmail, setLastEmail] = useState(params.get('email') ?? '');
  const [identifying, startIdentify] = useTransition();
  // An 'unknown' identify result renders an inline choice card instead of
  // auto-bouncing to sign-up (parity with the native sign-in page). Users
  // who signed up on the OTHER channel — or typo'd — get a recovery path
  // rather than being pushed into creating a duplicate account.
  const [unknownEmail, setUnknownEmail] = useState<string | null>(null);
  const isLoading = signInMutation.isPending;
  const isRedirecting = signInMutation.isSuccess;
  const captchaLoading = !captcha.isReady;

  const routeToSignUp = useCallback(
    (email: string, hint?: IdentifyAccountResult) => {
      // method=password keeps the sign-up page on the flow the user chose
      // here (the sign-up container honors ?method when that provider is on).
      const params = new URLSearchParams({ email, method: 'password' });
      if (hint?.kind === 'guest_upgrade') {
        if (hint.name) params.set('name', hint.name);
        if (hint.phoneHint) params.set('phone_hint', hint.phoneHint);
      }
      router.push(`${signUpPath}?${params.toString()}`);
    },
    [router, signUpPath],
  );

  const onIdentify = useCallback(
    (values: EmailOnlyValues) => {
      setLastEmail(values.email);
      setUnknownEmail(null);
      startIdentify(async () => {
        try {
          const result = await identifyAccountAction({ email: values.email });

          if (result.kind === 'guest_upgrade') {
            // Working upgrade path — keep the auto-route.
            routeToSignUp(values.email, result);
            return;
          }

          if (result.kind === 'unknown') {
            // No hard bounce to sign-up: offer "try phone instead" (they may
            // have signed up on the other channel) alongside "create account".
            setUnknownEmail(values.email);
            return;
          }

          setStep({ kind: 'password', email: values.email });
        } catch (error) {
          // On failure, fall back to letting the user enter a password —
          // worst case they hit the existing error path.
          monitoring.captureException(
            error instanceof Error ? error : new Error('Identify account failed'),
          );
          setStep({ kind: 'password', email: values.email });
        }
      });
    },
    [monitoring, routeToSignUp],
  );

  const onSubmitPassword = useCallback(
    async (credentials: z.infer<typeof PasswordSignInSchema>) => {
      try {
        const data = await signInMutation.mutateAsync({
          ...credentials,
          options: { captchaToken: captcha.token },
        });

        recordAuthMethod('password', { email: credentials.email });

        // Tenant association runs in the background; non-critical.
        associateCustomerWithTenantAction().catch(() => {});

        if (onSignIn) {
          onSignIn(data?.user?.id);
        }
      } catch (error) {
        const errorMessage =
          typeof error === 'string' ? error : (error as Error)?.message;
        const isCaptchaError = errorMessage?.toLowerCase().includes('captcha');

        monitoring.captureException(
          error instanceof Error ? error : new Error(errorMessage || 'Sign-in failed'),
          {
            extra: {
              errorType: isCaptchaError
                ? 'captcha_verification_failed'
                : 'sign_in_failed',
              authMethod: 'password',
            },
          },
        );
      } finally {
        captcha.reset();
      }
    },
    [captcha, onSignIn, signInMutation, recordAuthMethod, monitoring],
  );

  if (step.kind === 'email') {
    return (
      <>
        {unknownEmail ? (
          <UnknownAccountCard
            channel="email"
            onTryOtherMethod={onSwitchToPhone}
            onCreateAccount={() => routeToSignUp(unknownEmail)}
            onDismiss={() => setUnknownEmail(null)}
          />
        ) : null}

        <EmailIdentifyForm
          onSubmit={onIdentify}
          loading={identifying}
          defaultEmail={lastEmail}
          captchaField={captcha.field}
        />

        {footer}
      </>
    );
  }

  return (
    <>
      <AuthErrorAlert error={signInMutation.error} />

      <PasswordSignInForm
        onSubmit={onSubmitPassword}
        loading={isLoading}
        redirecting={isRedirecting}
        captchaLoading={captchaLoading}
        captchaField={captcha.field}
        prefilledEmail={step.email}
        onChangeEmail={() => {
          signInMutation.reset();
          setStep({ kind: 'email' });
        }}
      />
    </>
  );
}
